// お気に入り変更を 同一 Tab 内のリスナー と 他 Tab へ通知する
//
// 同一 Tab: 表示は state 由来の computed で自動更新されるため、リスナーは
//           Leaflet マーカー再描画などの命令的処理のみが使う。
// 他 Tab:   Service Worker 経由 (navigator.serviceWorker.controller.postMessage → sw.ts が
//           他 client へ中継) で通知し、controller が無い (初回訪問 / prompt 待ち / 未登録) 間は
//           BroadcastChannel で代替する。IndexedDB は Tab 間で共有されているので
//           受信側は IndexedDB から state を読み直すだけでよい。

export type FavoriteChangedKind = "circle" | "booth" | "colorName";

export type FavoriteChangedMessage = {
  type: typeof FAVORITE_CHANGED_MESSAGE_TYPE
  kind: FavoriteChangedKind
  eventId: number
  lastUpdated: number
  /** 送信元 Tab の id。自分自身からの通知を無視するために使う */
  origin: string
};

export const FAVORITE_CHANGED_MESSAGE_TYPE = "FAVORITE_CHANGED" as const;
export const FAVORITE_SYNC_CHANNEL_NAME = "favorite-sync";

type FavoriteChangedListener = () => void;

/** Tab 固有 id (module スコープで 1 回だけ生成) */
const selfOrigin: string = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
  ? crypto.randomUUID()
  : `${Date.now()}-${Math.random()}`;

/** kind ごとのリスナー (SPA なので Tab 内で 1 インスタンス) */
const listeners: Record<FavoriteChangedKind, Set<FavoriteChangedListener>> = {
  circle: new Set(),
  booth: new Set(),
  colorName: new Set(),
};

/** kind ごとの IndexedDB 再読込の進行状況 (連続受信の合流用) */
const reloadStatus: Record<FavoriteChangedKind, { running: boolean, pending: boolean }> = {
  circle: { running: false, pending: false },
  booth: { running: false, pending: false },
  colorName: { running: false, pending: false },
};

let fallbackChannel: BroadcastChannel | undefined = undefined;
const getFallbackChannel = (): BroadcastChannel | undefined => {
  if (fallbackChannel === undefined && typeof BroadcastChannel !== "undefined") {
    fallbackChannel = new BroadcastChannel(FAVORITE_SYNC_CHANNEL_NAME);
  }
  return fallbackChannel;
};

export const isFavoriteChangedMessage = (data: unknown): data is FavoriteChangedMessage => {
  if (typeof data !== "object" || data === null) {
    return false;
  }
  const record = data as Record<string, unknown>;
  return record.type === FAVORITE_CHANGED_MESSAGE_TYPE
    && (record.kind === "circle" || record.kind === "booth" || record.kind === "colorName")
    && typeof record.eventId === "number"
    && typeof record.origin === "string";
};

export const useFavoriteSync = () => {
  const config = useCirclemsConfig().get();

  const reloadFromDb = async (kind: FavoriteChangedKind): Promise<void> => {
    switch (kind) {
      case "circle":
        await useCircleFavoritesState().reloadFromDb();
        break;
      case "booth":
        await useBoothFavoritesState().reloadFromDb();
        break;
      case "colorName":
        await useCircleFavoriteColorState().reloadFromDb();
        break;
    }
  };

  /** 同一 Tab 内のリスナーを呼ぶ */
  const notifyLocal = (kind: FavoriteChangedKind) => {
    for (const listener of listeners[kind]) {
      try {
        listener();
      }
      catch (e) {
        // eslint-disable-next-line no-console
        if (import.meta.dev) console.warn("[favoriteSync] listener failed", e);
      }
    }
  };

  /** 他 Tab へ通知する。controller があれば SW 経由、無ければ BroadcastChannel */
  const broadcast = (message: FavoriteChangedMessage) => {
    if (!import.meta.client) {
      return;
    }
    try {
      const controller = navigator.serviceWorker?.controller;
      if (controller) {
        controller.postMessage(message);
        return;
      }
      getFallbackChannel()?.postMessage(message);
    }
    catch (e) {
      // 通知に失敗しても visibilitychange の安全網で一致させる
      // eslint-disable-next-line no-console
      if (import.meta.dev) console.warn("[favoriteSync] broadcast failed", e);
    }
  };

  /**
   * お気に入りの変更を通知する。IndexedDB への書き込み完了後に呼ぶこと
   */
  const publish = (kind: FavoriteChangedKind, lastUpdated: number) => {
    notifyLocal(kind);
    broadcast({
      type: FAVORITE_CHANGED_MESSAGE_TYPE,
      kind,
      eventId: config.eventId,
      lastUpdated,
      origin: selfOrigin,
    });
  };

  /**
   * IndexedDB から読み直してリスナーへ通知する。
   * 実行中に再要求された場合は完了後にもう 1 回だけ実行する (import 連打などの合流)
   */
  const reloadAndNotify = async (kind: FavoriteChangedKind): Promise<void> => {
    const status = reloadStatus[kind];
    if (status.running) {
      status.pending = true;
      return;
    }
    status.running = true;
    try {
      do {
        status.pending = false;
        await reloadFromDb(kind);
        notifyLocal(kind);
      } while (status.pending);
    }
    finally {
      status.running = false;
    }
  };

  /**
   * 他 Tab からの通知を受け取る (SW message / BroadcastChannel の両方から呼ばれる)
   */
  const receive = async (data: unknown): Promise<void> => {
    if (!isFavoriteChangedMessage(data)) {
      return;
    }
    if (data.origin === selfOrigin || data.eventId !== config.eventId) {
      return;
    }
    await reloadAndNotify(data.kind);
  };

  /**
   * 変更通知を購読する。effect scope 内で呼ばれた場合は scope 破棄時に自動解除する
   * @returns 購読解除関数
   */
  const onFavoriteChanged = (kind: FavoriteChangedKind, listener: FavoriteChangedListener): (() => void) => {
    listeners[kind].add(listener);
    const unsubscribe = () => {
      listeners[kind].delete(listener);
    };
    if (getCurrentScope()) {
      onScopeDispose(unsubscribe);
    }
    return unsubscribe;
  };

  return ({
    publish,
    receive,
    reloadAndNotify,
    onFavoriteChanged,
    getFallbackChannel,
    /** テスト用 */
    selfOrigin,
  });
};
